/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';

/**
 * RGBARRY-CUSTOMIZATION (W-23789028): Google Tag Manager bootstrap, rendered as nonce'd
 * inline <script> tags in the document <head>. This is the loader the ga4-datalayer adapter
 * assumes exists — the adapter only pushes to `window.dataLayer`; nothing loads GTM until this
 * runs. Requirements are per docs/ASSESSMENT-GOOGLE-TAG-MANAGER.md ("GTM Bootstrap"):
 *
 *  - Initialize `window.dataLayer` before gtm.js loads (so pre-hydration pushes aren't lost).
 *  - Establish Consent Mode defaults BEFORE the GTM bootstrap (Google tags respect the last
 *    default set before gtm.js). Production MUST default everything to `denied` until the
 *    binary GtmConsentBridge grants storage; `qaConsentGranted` is a QA-only override. This
 *    default is SKIPPED entirely when `trueVaultEnabled` — TrueVault's CMP bootstrap
 *    (TrueVaultConsent), rendered earlier in <head>, already set its own GRANTED default (US
 *    opt-out, enabled-by-default), and emitting our `denied` default after it would override it.
 *  - Reuse the CSP nonce already produced by the root layout (no `unsafe-inline`).
 *  - Never load on an unintended host: `containerId` is env-gated in config.server.ts, and a
 *    runtime `allowedHosts` check stops a cached SSR shell served on the wrong host (local dev,
 *    MRT preview) from pulling GTM.
 */

export interface GtmBootstrapConfig {
    containerId: string;
    auth: string;
    preview: string;
    allowedHosts: string[];
    qaConsentGranted: boolean;
}

export function GtmBootstrap({
    gtm,
    nonce,
    trueVaultEnabled = false,
}: {
    gtm?: GtmBootstrapConfig;
    nonce?: string;
    // When TrueVault is the active CMP it emits its OWN Consent Mode default (granted, US
    // opt-out) ahead of this bootstrap in <head>; this flag suppresses our default so the two
    // don't collide. Passed from root.tsx via isTrueVaultEnabled(consent.truevault.scriptUrl).
    trueVaultEnabled?: boolean;
}): ReactElement | null {
    const containerId = gtm?.containerId?.trim();
    // Env-gate: with no container configured the loader renders nothing, so environments that
    // don't opt in (and the default MRT bundle) never touch GTM.
    if (!containerId) return null;

    const auth = gtm?.auth?.trim() ?? '';
    const preview = gtm?.preview?.trim() ?? '';
    const allowedHosts = gtm?.allowedHosts ?? [];
    const consentState = gtm?.qaConsentGranted ? 'granted' : 'denied';

    // Consent Mode defaults — pushed before gtm.js so every downstream Google tag inherits them.
    const consentScript = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){window.dataLayer.push(arguments);}
        gtag('consent','default',{'ad_storage':'${consentState}','ad_user_data':'${consentState}','ad_personalization':'${consentState}','analytics_storage':'${consentState}'});
    `;

    // JSON-encode every interpolated value so it can't break out of the string literal.
    const containerIdJson = JSON.stringify(containerId);
    const allowedHostsJson = JSON.stringify(allowedHosts);
    // GTM Environment params select the non-Live (e.g. "SFNext Staging") environment. Only added
    // when both auth + preview are present; otherwise the Live environment loads.
    const envParams =
        auth && preview
            ? `+'&gtm_auth='+${JSON.stringify(auth)}+'&gtm_preview='+${JSON.stringify(preview)}+'&gtm_cookies_win=x'`
            : '';

    const loaderScript = `
        (function(){
            var allowed = ${allowedHostsJson};
            if (allowed.length && allowed.indexOf(window.location.host) === -1) return;
            (function(w,d,s,l,i){
                w[l]=w[l]||[];
                w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});
                var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
                j.async=true;
                j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl${envParams};
                var n=d.querySelector('script[nonce]');
                if(n&&n.nonce){j.setAttribute('nonce',n.nonce);}
                f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer',${containerIdJson});
        })();
    `;

    // no-danger is intentional here: injecting the GTM bootstrap as inline <script> is the
    // documented pattern (same as root.tsx's __APP_CONFIG__ script, which the lint config
    // exempts). Content is code we author from JSON-encoded config, never user input, and the
    // CSP nonce gates execution.
    return (
        <>
            {/* Consent Mode default. SKIPPED when TrueVault is the CMP: TrueVaultConsent (rendered
                earlier in <head>) already established a GRANTED default (US opt-out, enabled-by-default),
                and emitting our production `denied` default here would override it. When TrueVault is
                off this is the sole default (denied until GtmConsentBridge grants). */}
            {!trueVaultEnabled && (
                // oxlint-disable-next-line react/no-danger
                <script nonce={nonce} suppressHydrationWarning dangerouslySetInnerHTML={{ __html: consentScript }} />
            )}
            {/* oxlint-disable-next-line react/no-danger */}
            <script nonce={nonce} suppressHydrationWarning dangerouslySetInnerHTML={{ __html: loaderScript }} />
        </>
    );
}
